import React from 'react';
import { SponsorData, SponsorTypeData } from '../types';
import { Handshake, Pencil, Trash2, Tag, ExternalLink } from 'lucide-react';

interface SponsorListProps {
  sponsors: SponsorData[];
  onEditSponsor: (sponsor: SponsorData) => void;
  onDeleteSponsor: (sponsorId: string) => void;
  onToggleSponsor: (sponsorId: string, isActive: boolean) => void;
  sponsorTypes: SponsorTypeData[];
}

const MaybeFileImage: React.FC<{
  file?: File | null;
  url?: string | null;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
}> = ({ file, url, alt, className, style }) => {
  const [src, setSrc] = React.useState<string | null>(null);
  const objectUrlRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file);
      objectUrlRef.current = objectUrl;
      setSrc(objectUrl);
    } else if (typeof url === 'string' && url.length > 0) {
      setSrc(url);
    } else {
      setSrc(null);
    }

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [file, url]);

  if (!src) return null;
  return <img src={src} alt={alt} className={className} style={style} />;
};

const SponsorList: React.FC<SponsorListProps> = ({
  sponsors,
  onEditSponsor,
  onDeleteSponsor,
  onToggleSponsor,
  sponsorTypes
}) => {
  if (sponsors.length === 0) {
    return (
      <div style={{ padding: "32px 16px", textAlign: "center", borderRadius: 14, background: 'var(--v3-bg-subtle, rgba(255,255,255,0.02))', border: '1px dashed var(--v3-border-medium, rgba(255,255,255,0.08))' }}>
        <Handshake size={28} color="var(--v3-text-faint, rgba(255,255,255,0.2))" style={{ margin: "0 auto 8px auto", display: "block" }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--v3-text-main, #fff)' }}>No sponsors added yet</div>
        <p style={{ fontSize: 11, color: 'var(--v3-text-muted, rgba(255,255,255,0.4))', marginTop: 4, marginBottom: 0 }}>Add sponsors to showcase their support for your event.</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {sponsors.map((sponsor) => (
        <div
          key={sponsor.id || sponsor.sponsorId}
          style={{
            padding: 16,
            borderRadius: 14,
            background: "var(--v3-bg-subtle, rgba(255,255,255,0.03))",
            border: "1px solid var(--v3-border-subtle, rgba(255,255,255,0.07))",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "1 1 240px", minWidth: 0 }}>
            {(sponsor.thumb || sponsor.thumbImageUrl) ? (
              <MaybeFileImage
                file={sponsor.thumb instanceof File ? sponsor.thumb : undefined}
                url={sponsor.thumbImageUrl || (typeof sponsor.thumb === 'string' ? sponsor.thumb : undefined)}
                alt={sponsor.name}
                style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 12, flexShrink: 0, border: '1px solid rgba(255,255,255,0.1)' }}
              />
            ) : (
              <div
                style={{
                  width: 46,
                  height: 46,
                  borderRadius: 12,
                  background: "rgba(238, 63, 124, 0.12)",
                  border: "1px solid rgba(238, 63, 124, 0.25)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#EE3F7C",
                  flexShrink: 0,
                }}
              >
                <Handshake size={20} />
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--v3-text-main, #fff)" }}>
                {sponsor.name}
              </div>

              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#EE3F7C", background: "rgba(238,63,124,0.1)", padding: "2px 8px", borderRadius: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <Tag size={11} />
                  {sponsorTypes.find(type => String(type.sponsorTypeId) === String(sponsor.sponsorTypeId))?.title || 'Sponsor'}
                </span>
              </div>

              {sponsor.link && (
                <a
                  href={sponsor.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 11, color: "var(--v3-text-muted, rgba(255,255,255,0.5))", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <ExternalLink size={11} /> {sponsor.link}
                </a>
              )}
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
              type="button"
              className="v3-btn-secondary"
              style={{ fontSize: 12, padding: "6px 12px" }}
              onClick={() => onEditSponsor(sponsor)}
              title="Edit Sponsor"
            >
              <Pencil size={13} /> Edit
            </button>

            <button
              type="button"
              className="v3-btn-secondary"
              style={{ fontSize: 12, padding: "6px 12px", color: "#FF5D73", borderColor: "rgba(255,93,115,0.2)" }}
              onClick={() => onDeleteSponsor(sponsor.sponsorId || sponsor.id || '')}
              title="Delete Sponsor"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SponsorList;